/**
 * @component ry-tile-radio
 * @category  form
 * @summary   Large card-style radio button (Archetype A: stateful binary inside a single-select group). Wraps a visually-hidden `<input type="radio">` with content slots for icon, heading, meta, and optional badge/pill. Sharing `[name]` across tiles forms a radio group — only one can be checked at a time. ry-ui-only — no Fomantic equivalent.
 *
 * @attribute name       string?              Form field name. REQUIRED for radio-group behavior — tiles with the same name auto-uncheck siblings on selection.
 * @attribute value      string?              Submitted value when checked. No default for radio (use a meaningful value per tile).
 * @attribute checked    boolean   default=false   Initial checked state. If multiple same-name tiles set `[checked]` at mount, native radio semantics + the auto-uncheck handler reconcile to a single winner.
 * @attribute disabled   boolean   default=false   Non-interactive. Pure attribute hook on the inner input.
 * @attribute icon       string?              Icon name from the registry. Renders into the icon slot above the heading.
 * @attribute heading    string?              Bold title text shown next to the icon.
 * @attribute meta       string?              Smaller description text below the heading.
 * @attribute badge      string?              Right-side badge text (e.g. "Included", "Most popular").
 * @attribute pill       'good' | 'bad' ?     Inline pill style next to the heading. Combine with [pill-text] to render.
 * @attribute pill-text  string?              Pill label (e.g. "recommended"). Without [pill], no pill renders.
 * @attribute warn       boolean   default=false   Amber-tinted "not recommended" visual variant. Pure CSS hook.
 *
 * @property  checked    boolean   Live checked state (mirrors the inner input).
 * @property  value      string    Read-only — the inner input's value attribute.
 *
 * @event ry:beforechange
 *        detail: { value: string, checked: true, previous?: string }
 *        fires:  from the inner input's `click` (tile click, Space) before the tile checks — only when it is not already checked. `previous` is the value of the group's currently-checked `<ry-tile-radio>` (omitted when none / no `[name]`).
 *        cancelable: preventDefault() cancels the inner input's click — the platform leaves this tile unchecked, the group's current selection stands, and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string, checked: boolean }
 *        fires:  on user click of the visible tile (which clicks the hidden input). Fires once per state change. detail.checked is the new state.
 *
 * @keyboard  Tab moves focus to the inner input; Space toggles. Native radio keyboard semantics apply (Arrow keys cycle through same-name siblings within a form).
 *
 * @state     `[checked]` attribute mirrors the inner input. After click on a same-name sibling, the previously-checked tile's `[checked]` attribute is removed automatically (CSS / querySelectorAll(`[checked]`) stays accurate).
 *
 * @content   None — `<ry-tile-radio>` is configured entirely via attributes. innerHTML is replaced at setup with the rendered tile markup.
 *
 * @dom
 *   ry-tile-radio[name?][value?][checked?][disabled?]
 *     > input.ry-tile__input  type="radio"  (visually hidden)
 *     > span.ry-tile           [data-ry-target="tile"]
 *       > span.ry-tile__icon       (only when [icon])
 *       > span.ry-tile__heading    (only when [heading])    {heading} <pill>
 *       > span.ry-tile__meta       (only when [meta])       {meta}
 *       > span.ry-tile__badge      (only when [badge])      {badge}
 *
 * @gotcha  `name` is REQUIRED for radio-group behavior. Without it, tiles are independent — clicking one does not uncheck others. Use a shared name to opt into single-selection semantics.
 *
 * @gotcha  The inner input is `position: absolute` + visually hidden; the host has `position: relative` set via theme CSS so the input doesn't escape its bounds. Don't override this on the host.
 *
 * @gotcha  Setting `[checked]` on multiple same-name tiles at mount: native radio semantics make only ONE checked (the last one parsed wins), and the auto-uncheck handler reconciles the others' `[checked]` attribute. Don't rely on attribute order for the winner.
 *
 * @gotcha  `setup()` is idempotent — the renderTile helper bails if `[data-ry-target="tile"]` exists. To override markup, mount with the target already authored.
 *
 * @example basic
 * ```html
 * <ry-tile-radio name="plan" value="free" checked heading="Free"  meta="Up to 5 projects."></ry-tile-radio>
 * <ry-tile-radio name="plan" value="pro"          heading="Pro"   meta="Unlimited projects."></ry-tile-radio>
 * <ry-tile-radio name="plan" value="ent"          heading="Enterprise" meta="Talk to sales."></ry-tile-radio>
 * ```
 *
 * @example with-icon-and-badge
 * ```html
 * <ry-tile-radio name="funding" value="insured"
 *                icon="shield" heading="Fully insured" meta="Carrier underwrites the plan."
 *                badge="Recommended"></ry-tile-radio>
 * ```
 *
 * @example with-pill
 * ```html
 * <ry-tile-radio name="x" value="a" heading="Option A"
 *                pill="good" pill-text="popular"></ry-tile-radio>
 * <ry-tile-radio name="x" value="b" heading="Option B"
 *                pill="bad"  pill-text="legacy"></ry-tile-radio>
 * ```
 *
 * @example warn-variant
 * ```html
 * <ry-tile-radio name="risk" value="yes" warn heading="Self-fund"
 *                meta="Employer carries the full risk."></ry-tile-radio>
 * ```
 *
 * @tutorial mount-renders-input-and-tile
 * ```js
 * const el = mount('<ry-tile-radio name="x" value="a" heading="A"></ry-tile-radio>');
 * await expectEqual(!!el.querySelector('input[type="radio"]'), true, 'inner radio input rendered');
 * await expectEqual(!!el.querySelector('[data-ry-target="tile"]'), true, 'tile slot rendered');
 * ```
 *
 * @tutorial heading-meta-badge-render-slots
 * ```js
 * const el = mount('<ry-tile-radio name="x" value="a" heading="A title" meta="A meta" badge="A badge"></ry-tile-radio>');
 * await expectEqual(el.querySelector('.ry-tile__heading')?.textContent?.includes('A title'), true);
 * await expectEqual(el.querySelector('.ry-tile__meta')?.textContent, 'A meta');
 * await expectEqual(el.querySelector('.ry-tile__badge')?.textContent, 'A badge');
 * ```
 *
 * @tutorial click-checks-and-fires-ry-change
 * ```js
 * // Wrong assumption: clicking the host fires ry:change. The hidden input
 * // is what carries the event — drive it directly in tests.
 * const el = mount('<ry-tile-radio name="x" value="a" heading="A"></ry-tile-radio>');
 * const input = el.querySelector('input');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail));
 * input.checked = true;
 * input.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(el.checked, true);
 * await expectAttr(el, 'checked', '');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 'a');
 * await expectEqual(fired[0].checked, true);
 * ```
 *
 * @tutorial same-name-siblings-auto-uncheck
 * ```js
 * // Wrong assumption: native radio takes care of everything. The inner
 * // input is auto-unchecked by the browser, but the HOST's [checked]
 * // attribute would lag — ry-tile-radio syncs it on ry:change.
 * const wrap = mount(`
 *   <div>
 *     <ry-tile-radio id="a" name="grp" value="a" checked heading="A"></ry-tile-radio>
 *     <ry-tile-radio id="b" name="grp" value="b" heading="B"></ry-tile-radio>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const bInput = b.querySelector('input');
 * bInput.checked = true;
 * bInput.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(a.hasAttribute('checked'), false, 'A [checked] attribute removed');
 * await expectAttr(b, 'checked', '', 'B [checked] attribute applied');
 * ```
 *
 * @tutorial different-names-do-not-coordinate
 * ```js
 * const wrap = mount(`
 *   <div>
 *     <ry-tile-radio id="a" name="g1" value="a" checked heading="A"></ry-tile-radio>
 *     <ry-tile-radio id="b" name="g2" value="b" checked heading="B"></ry-tile-radio>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * await expectAttr(a, 'checked', '');
 * await expectAttr(b, 'checked', '', 'independent groups coexist');
 * ```
 *
 * @tutorial programmatic-checked-setter
 * ```js
 * const el = mount('<ry-tile-radio name="x" value="a" heading="A"></ry-tile-radio>');
 * el.checked = true;
 * await expectEqual(el.checked, true);
 * await expectAttr(el, 'checked', '');
 * el.checked = false;
 * await expectEqual(el.checked, false);
 * await expectEqual(el.hasAttribute('checked'), false);
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the inner input's click —
 * // the tile stays unchecked, the group keeps its selection, ry:change is silent.
 * const wrap = mount(`
 *   <div>
 *     <ry-tile-radio id="a" name="grp" value="a" checked heading="A"></ry-tile-radio>
 *     <ry-tile-radio id="b" name="grp" value="b" heading="B"></ry-tile-radio>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const bInput = b.querySelector('input');
 * const changed = [];
 * b.addEventListener('ry:change', () => changed.push(1));
 * let seen = null;
 * b.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * bInput.click();
 * await expectEqual(seen.previous, 'a', 'detail.previous is the current group value');
 * await expectEqual(b.hasAttribute('checked'), false, 'vetoed: B stays unchecked');
 * await expectAttr(a, 'checked', '', 'vetoed: A stays the selection');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * bInput.click();
 * await expectAttr(b, 'checked', '', 'without the veto, B is selected');
 * await expectEqual(a.hasAttribute('checked'), false, 'and A is unchecked');
 * await expectEqual(changed.length, 1);
 * ```
 *
 * @see ry-tile-checkbox
 * @see ry-toggle-button
 */

import { RyElement } from '../core/ry-element.js';
import { renderTile, type TileType } from './ry-tile-shared.js';

export class RyTileRadio extends RyElement {
  static observedAttributes = ['checked', 'disabled'] as const;

  #input: HTMLInputElement | null = null;

  setup(): void {
    renderTile(this, 'radio');
    this.#input = this.$<HTMLInputElement>('input.ry-tile__input');
    if (this.#input) {
      this.on(this.#input, 'click', this.#handleClick);
      this.on(this.#input, 'change', this.#handleChange);
    }
  }

  // The interception point. A radio's `click` pre-activation has ALREADY
  // checked this input (and unchecked the native siblings) by the time the
  // event dispatches; cancelling the click makes the platform restore the
  // group and no native `change` fires. The host `[checked]` attributes are
  // still the committed state here (they only sync in #handleChange), so the
  // group's previous selection is read from them.
  #handleClick = (event: MouseEvent): void => {
    if (!this.#input) return;
    const previous = this.#checkedSibling();
    // Clicking the already-selected tile is a no-op — no change, no interception.
    if (previous === this || (!previous && this.hasAttribute('checked'))) return;
    const detail: { value: string; checked: true; previous?: string } = { value: this.#input.value, checked: true };
    if (previous) detail.previous = previous.value;
    if (!this.emit('beforechange', detail)) event.preventDefault();
  };

  /** The `<ry-tile-radio>` in this group whose host `[checked]` is set (committed state), if any. */
  #checkedSibling(): RyTileRadio | null {
    const name = this.#input?.name;
    if (!name) return null;
    const root = this.getRootNode() as Document | ShadowRoot;
    return root.querySelector<RyTileRadio>(`ry-tile-radio[name="${name}"][checked]`);
  }

  #handleChange = (): void => {
    if (!this.#input) return;
    const checked = this.#input.checked;
    if (checked) this.setAttribute('checked', '');
    else this.removeAttribute('checked');

    this.emit('change', { value: this.#input.value, checked });

    // Native radios with the same `name` auto-uncheck their siblings, but the
    // host attribute won't reflect that. Sync sibling attributes so CSS /
    // querying by [checked] stays accurate.
    if (checked && this.#input.name) {
      const root = this.getRootNode() as Document | ShadowRoot;
      root.querySelectorAll<RyTileRadio>(
        `ry-tile-radio[name="${this.#input.name}"]`
      ).forEach(el => {
        if (el !== this) el.removeAttribute('checked');
      });
    }
  };

  get checked(): boolean { return this.#input?.checked ?? false; }
  set checked(v: boolean) {
    if (!this.#input) return;
    this.#input.checked = v;
    if (v) this.setAttribute('checked', '');
    else this.removeAttribute('checked');
  }

  get value(): string { return this.#input?.value ?? ''; }
}

customElements.define('ry-tile-radio', RyTileRadio);

export type { TileType };
